// =============================================================================
// Inventory Management — warehouses, their contract terms and what they have billed.
//
// Two kinds of place hold stock: an operator's warehouse, which charges by the pallet and sends monthly
// invoices, and a laydown yard on somebody else's site, which charges nothing and has no ceiling. The
// difference matters on every screen here, so it is a field rather than a convention.
// =============================================================================

import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { strings } from '/imports/strings';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Button } from '/imports/ui/Button';
import { useMethod } from '/imports/hooks';
import { inventoryListWarehouses, inventoryWarehouseDetail } from '/imports/api/inventory';
import {
  EmptyRow, Fact, InventoryPageStyled, Tile, count, day, money, orDash, useInventoryAccess,
} from '/imports/ui/InventoryShared';

/** Every place the org holds stock, with on-hand rollups and headroom against the storage contract. */
export const InventoryWarehousesPage = () => {
  const { canAccess } = useInventoryAccess();
  const navigate = useNavigate();
  const [rows, loading] = useMethod(inventoryListWarehouses, undefined, []);
  if (!canAccess) return <NotPermittedPage />;

  return (
    <InventoryPageStyled className="page">
      <PageHeading left={<h1>{strings.InventoryWarehouses()}</h1>} />
      <PageBody>
        <div className="scroll-x">
          <table className="inv">
            <thead>
              <tr>
                <th>{strings.Name()}</th>
                <th>{strings.Address()}</th>
                <th>{strings.State()}</th>
                <th>{strings.InventoryOperator()}</th>
                <th className="num">{strings.InventoryLineItems()}</th>
                <th className="num">{strings.InventoryUnitsOnHand()}</th>
                <th className="num">{strings.InventoryPalletsAvailable()}</th>
                <th className="num">{strings.InventoryTotalValue()}</th>
                <th className="num">{strings.InventoryCapacity()}</th>
                <th className="num">{strings.InventoryUtilization()}</th>
                <th className="num">{strings.InventoryInvoicedToDate()}</th>
              </tr>
            </thead>
            <tbody>
              {loading && !rows && <tr><td colSpan={11}><LoadingFlex /></td></tr>}
              {(rows || []).map((w) => (
                <tr key={w._id} className="clickable" onClick={() => navigate(`/inventory/warehouses/${w._id}`)}>
                  <td>{w.code} <span className="muted">{w.city}</span></td>
                  <td>{w.address}</td>
                  <td>{w.state}</td>
                  <td>{w.isLaydownYard ? <span className="muted">{strings.InventoryLaydownYard()}</span> : orDash(w.operator)}</td>
                  <td className="num">{count(w.lineItems)}</td>
                  <td className="num">{count(w.unitsOnHand)}</td>
                  <td className="num">{count(w.palletsAvailable)}</td>
                  <td className="num">{money(w.value)}</td>
                  <td className="num">{count(w.capacityUnits)}</td>
                  <td className="num">{w.utilizationPct == null ? orDash(undefined) : `${w.utilizationPct}%`}</td>
                  <td className="num">{money(w.invoicedTotal, true)}</td>
                </tr>
              ))}
              {!loading && !rows?.length && <EmptyRow colSpan={11} />}
            </tbody>
          </table>
        </div>
      </PageBody>
    </InventoryPageStyled>
  );
};

/** One warehouse: contract rates, what it holds, which lots ship there, and its invoices. */
export const InventoryWarehousePage = () => {
  const { _id } = useParams<{ _id: string }>();
  const { canAccess } = useInventoryAccess();
  const [detail, loading] = useMethod(inventoryWarehouseDetail, { warehouseId: _id as string }, [_id]);
  if (!canAccess) return <NotPermittedPage />;
  if (loading && !detail) return <InventoryPageStyled className="page"><PageBody><LoadingFlex /></PageBody></InventoryPageStyled>;
  if (!detail) return <InventoryPageStyled className="page"><PageBody><div className="empty">{strings.NoResults()}</div></PageBody></InventoryPageStyled>;

  const { row, invoices, lots } = detail;
  const rates = row.rates;

  return (
    <InventoryPageStyled className="page">
      <PageHeading
        left={<h1>{row.code} — {row.city}, {row.state}</h1>}
        right={<Link to={`/inventory/pallet-inventory?warehouse=${row._id}`}><Button className="secondary compact">{strings.InventoryOpenInPalletInventory()}</Button></Link>}
      />
      <PageBody>
        <div className="tiles">
          <Tile label={strings.InventoryPalletsOnHand()} value={count(row.palletsOnHand)} sub={`${count(row.lineItems)} ${strings.InventoryLineItems().toLowerCase()}`} />
          <Tile label={strings.InventoryUnitsOnHand()} value={count(row.unitsOnHand)} sub={money(row.value)} />
          <Tile label={strings.InventoryPalletsAvailable()} value={count(row.palletsAvailable)} sub={`${count(row.modulesAvailable)} ${strings.InventoryModulesAvailable().toLowerCase()}`} />
          <Tile label={strings.InventoryUtilization()} value={row.utilizationPct == null ? orDash(undefined) : `${row.utilizationPct}%`} sub={row.capacityUnits ? `${count(row.capacityUnits)} ${strings.InventoryCapacity().toLowerCase()}` : undefined} />
          <Tile label={strings.InventoryInvoicedToDate()} value={money(row.invoicedTotal, true)} sub={`${invoices.filter((i) => i.status !== 'paid').length} ${strings.InventoryUnpaid().toLowerCase()}`} />
        </div>

        <div className="panels">
          <div className="panel-card">
            <h2>{strings.InventoryWarehouse()}</h2>
            <div className="facts">
              <Fact label={strings.Address()} value={[row.address, row.city, row.state, row.zip].filter(Boolean).join(', ')} />
              <Fact label={strings.InventoryOperator()} value={row.isLaydownYard ? strings.InventoryLaydownYard() : row.operator} />
              <Fact label={strings.InventoryOperatorProjectNumber()} value={row.operatorProjectNumber} />
              <Fact label={strings.Name()} value={row.contactName} />
              <Fact label={strings.Phone()} value={row.contactPhone} />
              <Fact label={strings.InventoryReceivingWindow()} value={row.receivingWindow} />
              <Fact label={strings.InventoryStorageType()} value={row.storageType} />
              <Fact label={strings.InventoryBonded()} value={row.bonded == null ? undefined : row.bonded ? strings.Yes() : strings.No()} />
              <Fact label={strings.InventoryScheduleAPallets()} value={count(row.scheduleAPallets)} />
              <Fact label={strings.InventoryScheduleAMw()} value={row.scheduleAMw == null ? undefined : `${row.scheduleAMw} MW`} />
            </div>
            {!!row.notes && <div className="note">{row.notes}</div>}
          </div>

          {rates && (
            <div className="panel-card">
              <h2>{strings.InventoryRates()}</h2>
              <div className="facts">
                <Fact label={strings.InventoryInFee()} value={money(rates.inFee, true)} />
                <Fact label={strings.InventoryOutFee()} value={money(rates.outFee, true)} />
                <Fact label={strings.InventoryStoragePerPalletMonth()} value={money(rates.storagePerPalletMonth, true)} />
                <Fact label={strings.InventoryScanFee()} value={money(rates.scanFee, true)} />
                <Fact label={strings.InventoryDunnagePerTruck()} value={money(rates.dunnagePerTruck, true)} />
                <Fact label={strings.InventoryLaborPerHour()} value={money(rates.laborPerHour, true)} />
                <Fact label={strings.InventoryOvertimePerHour()} value={money(rates.overtimePerHour, true)} />
                <Fact label={strings.InventoryMonthlyMinimum()} value={money(rates.monthlyMinimum)} />
                <Fact label={strings.InventoryEscalator()} value={rates.escalatorPct == null ? undefined : `${(rates.escalatorPct * 100).toFixed(1)}%`} />
                <Fact label={strings.InventoryEscalatorEffective()} value={day(rates.escalatorEffective)} />
              </div>
            </div>
          )}
        </div>

        <h2 style={{ fontSize: '0.9rem', marginTop: '1.2rem' }}>{strings.InventoryLots()}</h2>
        <div className="scroll-x">
          <table className="inv">
            <thead>
              <tr>
                <th>{strings.InventoryPo()}</th>
                <th>{strings.InventoryContractNumber()}</th>
                <th>{strings.InventoryModuleModel()}</th>
                <th className="num">{strings.InventoryModulesOrdered()}</th>
                <th className="num">{strings.InventoryPalletsLoaded()}</th>
                <th className="num">{strings.InventoryBookValue()}</th>
              </tr>
            </thead>
            <tbody>
              {lots.map((l) => (
                <tr key={l._id}>
                  <td><Link to={`/inventory/lots/${l._id}`}>{l.poNumber || l.contractNumber}</Link></td>
                  <td>{l.contractNumber}</td>
                  <td>{orDash(l.moduleModel)}</td>
                  <td className="num">{count(l.modulesOrdered)}</td>
                  <td className="num">{count(l.palletsLoaded)}</td>
                  <td className="num">{money(l.bookValue)}</td>
                </tr>
              ))}
              {!lots.length && <EmptyRow colSpan={6} />}
            </tbody>
          </table>
        </div>

        <h2 style={{ fontSize: '0.9rem', marginTop: '1.2rem' }}>{strings.InventoryInvoices()}</h2>
        <div className="scroll-x">
          <table className="inv">
            <thead>
              <tr>
                <th>{strings.InvoiceNumber()}</th>
                <th>{strings.Date()}</th>
                <th>{strings.InventoryServicePeriod()}</th>
                <th>{strings.DueDate()}</th>
                <th className="num">{strings.InventoryInvoiceTotal()}</th>
                <th className="num">{strings.InventoryAmountPaid()}</th>
                <th className="num">{strings.InventoryBalanceDue()}</th>
                <th>{strings.Status()}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i._id}>
                  <td>{i.invoiceNumber}</td>
                  <td>{day(i.invoiceDate)}</td>
                  <td>{day(i.servicePeriodStart)} – {day(i.servicePeriodEnd)}</td>
                  <td>{day(i.dueDate)}</td>
                  <td className="num">{money(i.total, true)}</td>
                  <td className="num">{money(i.amountPaid || 0, true)}</td>
                  <td className="num">{money((i.total || 0) - (i.amountPaid || 0), true)}</td>
                  <td>{i.status === 'paid' ? strings.Paid() : strings.InventoryUnpaid()}</td>
                </tr>
              ))}
              {!invoices.length && <EmptyRow colSpan={8} label={strings.InventoryNoInvoices()} />}
            </tbody>
            {!!invoices.length && (
              <tfoot>
                <tr>
                  <td colSpan={4}>{strings.Total()}</td>
                  <td className="num">{money(invoices.reduce((n, i) => n + (i.total || 0), 0), true)}</td>
                  <td className="num">{money(invoices.reduce((n, i) => n + (i.amountPaid || 0), 0), true)}</td>
                  <td className="num">{money(invoices.reduce((n, i) => n + ((i.total || 0) - (i.amountPaid || 0)), 0), true)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </PageBody>
    </InventoryPageStyled>
  );
};
